import type { Block, TableCell } from '@worldathletics/types';
import { normalizeColumnWidthInput } from '@worldathletics/utilities';
import { expandTableGrid } from './expand-table-grid';

type TableBlock = Extract<Block, { type: 'table' }>;
type TableColumn = NonNullable<TableBlock['columns']>[number];

function emptyCell(): TableCell {
  return { text: '' };
}

function normalizeSpan<T extends TableCell>(cell: T): T {
  const out = { ...cell };
  if (out.rowSpan === 1) delete out.rowSpan;
  if (out.colSpan === 1) delete out.colSpan;
  return out;
}

function gridColumnCount(table: TableBlock): number {
  const fromRows = Math.max(
    0,
    ...table.rows.map(r => {
      let w = 0;
      for (const c of r) w += c.colSpan ?? 1;
      return w;
    }),
  );
  const fromColumns = table.columns?.length ?? 0;
  return Math.max(fromRows, fromColumns);
}

function ensureColumnsArray(
  table: TableBlock,
  colCount: number,
): TableColumn[] {
  const columns = [...(table.columns ?? [])];
  while (columns.length < colCount) {
    columns.push({});
  }
  return columns;
}

/** Creates an empty rows×cols grid for a new table block. */
export function createTableRows(rows: number, cols: number): TableCell[][] {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => emptyCell()),
  );
}

export function getTableDimensions(table: TableBlock): {
  rows: number;
  cols: number;
} {
  const colCount = gridColumnCount(table);
  if (table.rows.length === 0) {
    return { rows: 0, cols: colCount || 0 };
  }
  const grid = expandTableGrid(table.rows, colCount || undefined);
  return { rows: grid.length, cols: grid[0]?.length ?? 0 };
}

/** Returns true when shrinking would discard non-empty cell text. */
export function tableResizeWouldLoseData(
  table: TableBlock,
  newRows: number,
  newCols: number,
): boolean {
  const { rows, cols } = getTableDimensions(table);
  if (newRows >= rows && newCols >= cols) return false;
  const colCount = gridColumnCount(table) || cols;
  const grid = expandTableGrid(table.rows, colCount || undefined);
  for (let r = 0; r < grid.length; r += 1) {
    for (const gc of grid[r]!) {
      if (!gc.isAnchor) continue;
      if (r >= newRows || gc.c >= newCols) {
        const plain = gc.cell.text.replace(/<[^>]*>/g, '').trim();
        if (plain.length > 0) return true;
      }
    }
  }
  return false;
}

/**
 * Resizes a table grid to newRows×newCols. Spans are flattened to anchor cells.
 * Caller should confirm when {@link tableResizeWouldLoseData} is true.
 */
export function resizeTable(
  table: TableBlock,
  newRows: number,
  newCols: number,
): TableBlock {
  const colCount = gridColumnCount(table);
  const grid =
    table.rows.length > 0
      ? expandTableGrid(table.rows, colCount || newCols)
      : [];

  const simplified: TableCell[][] = grid.map(row =>
    row.filter(gc => gc.isAnchor).map(gc => ({ ...gc.cell })),
  );

  while (simplified.length < newRows) {
    simplified.push(
      Array.from({ length: newCols }, () => emptyCell()),
    );
  }
  simplified.length = newRows;

  const rows = simplified.map(row => {
    const next = row.slice(0, newCols);
    while (next.length < newCols) {
      next.push(emptyCell());
    }
    return next.map(normalizeSpan);
  });

  let columns = table.columns;
  if (columns !== undefined) {
    columns = columns.slice(0, newCols);
    while (columns.length < newCols) {
      columns.push({});
    }
  }

  return { ...table, rows, ...(columns !== undefined ? { columns } : {}) };
}

export function insertRow(
  table: TableBlock,
  atIndex: number,
  where: 'above' | 'below',
): TableBlock {
  const insertAt = where === 'above' ? atIndex : atIndex + 1;
  const colCount = gridColumnCount(table);
  const grid = expandTableGrid(table.rows, colCount);

  const newRows: TableCell[][] = table.rows.map((row, r) =>
    row.map(cell => {
      const span = cell.rowSpan ?? 1;
      if (span > 1 && r < insertAt && r + span > insertAt) {
        return normalizeSpan({ ...cell, rowSpan: span + 1 });
      }
      return cell;
    }),
  );

  let coveredFromAbove = 0;
  if (insertAt > 0 && grid[insertAt - 1]) {
    for (const gc of grid[insertAt - 1]!) {
      const span = gc.cell.rowSpan ?? 1;
      if (gc.isAnchor && gc.r + span > insertAt) coveredFromAbove += 1;
    }
  }
  const newBlankRow: TableCell[] = [];
  for (let i = 0; i < colCount - coveredFromAbove; i += 1) {
    newBlankRow.push(emptyCell());
  }
  newRows.splice(insertAt, 0, newBlankRow);

  return { ...table, rows: newRows };
}

export function removeRow(table: TableBlock, atIndex: number): TableBlock {
  const newRows: TableCell[][] = table.rows
    .map((row, r) => {
      if (r === atIndex) return null;
      return row.map(cell => {
        const span = cell.rowSpan ?? 1;
        if (span > 1 && r < atIndex && r + span > atIndex) {
          return normalizeSpan({ ...cell, rowSpan: span - 1 });
        }
        return cell;
      });
    })
    .filter((r): r is TableCell[] => r !== null);
  return { ...table, rows: newRows };
}

export function insertColumn(
  table: TableBlock,
  atIndex: number,
  where: 'left' | 'right',
): TableBlock {
  const insertAt = where === 'left' ? atIndex : atIndex + 1;
  const colCount = gridColumnCount(table);
  const grid = expandTableGrid(table.rows, colCount);

  const newRows: TableCell[][] = table.rows.map((row, r) => {
    const newRow: TableCell[] = [];
    let c = 0;
    for (const cell of row) {
      while (c < colCount && grid[r]![c]!.isAnchor === false) c += 1;
      const span = cell.colSpan ?? 1;
      if (span > 1 && c < insertAt && c + span > insertAt) {
        newRow.push(normalizeSpan({ ...cell, colSpan: span + 1 }));
      } else if (c === insertAt) {
        newRow.push(emptyCell());
        newRow.push(cell);
      } else {
        newRow.push(cell);
      }
      c += span;
    }
    if (insertAt >= colCount && newRow.length === row.length) {
      newRow.push(emptyCell());
    }
    return newRow;
  });

  let newColumns = table.columns;
  if (newColumns) {
    newColumns = [...newColumns];
    newColumns.splice(insertAt, 0, {});
  }

  return { ...table, rows: newRows, columns: newColumns };
}

export function removeColumn(table: TableBlock, atIndex: number): TableBlock {
  const colCount = gridColumnCount(table);
  const grid = expandTableGrid(table.rows, colCount);

  const newRows: TableCell[][] = table.rows.map((row, r) => {
    const newRow: TableCell[] = [];
    let c = 0;
    for (const cell of row) {
      while (c < colCount && grid[r]![c]!.isAnchor === false) c += 1;
      const span = cell.colSpan ?? 1;
      if (c === atIndex && span === 1) {
        // drop
      } else if (c <= atIndex && c + span > atIndex && span > 1) {
        newRow.push(normalizeSpan({ ...cell, colSpan: span - 1 }));
      } else {
        newRow.push(cell);
      }
      c += span;
    }
    return newRow;
  });

  let newColumns = table.columns;
  if (newColumns) {
    newColumns = [...newColumns];
    newColumns.splice(atIndex, 1);
  }

  return { ...table, rows: newRows, columns: newColumns };
}

export type CellSelection = {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
};

export function mergeCells(
  table: TableBlock,
  sel: CellSelection,
): TableBlock {
  const r1 = Math.min(sel.r1, sel.r2);
  const r2 = Math.max(sel.r1, sel.r2);
  const c1 = Math.min(sel.c1, sel.c2);
  const c2 = Math.max(sel.c1, sel.c2);
  const colCount = gridColumnCount(table);
  const grid = expandTableGrid(table.rows, colCount);

  for (let r = r1; r <= r2; r += 1) {
    for (let c = c1; c <= c2; c += 1) {
      const gc = grid[r]![c]!;
      const anchor = gc.isAnchor ? gc : grid[gc.r]![gc.c]!;
      const h = anchor.cell.rowSpan ?? 1;
      const w = anchor.cell.colSpan ?? 1;
      if (
        anchor.r < r1 ||
        anchor.c < c1 ||
        anchor.r + h - 1 > r2 ||
        anchor.c + w - 1 > c2
      ) {
        throw new Error(
          `mergeCells: existing span at (${String(anchor.r)},${String(
            anchor.c,
          )}) crosses the selection boundary`,
        );
      }
    }
  }

  const anchorCell = grid[r1]![c1]!.cell;
  const mergedText: string[] = [];
  for (let r = r1; r <= r2; r += 1) {
    for (let c = c1; c <= c2; c += 1) {
      const gc = grid[r]![c]!;
      if (!gc.isAnchor) continue;
      const plain = gc.cell.text.replace(/<[^>]*>/g, '').trim();
      if (plain.length > 0) mergedText.push(gc.cell.text.trim());
    }
  }

  const mergedAnchor: TableCell = normalizeSpan({
    ...anchorCell,
    text: mergedText.join(' '),
    rowSpan: r2 - r1 + 1,
    colSpan: c2 - c1 + 1,
  });

  const newRows: TableCell[][] = table.rows.map((row, r) => {
    const newRow: TableCell[] = [];
    let c = 0;
    for (const cell of row) {
      while (c < colCount && grid[r]![c]!.isAnchor === false) c += 1;
      const span = cell.colSpan ?? 1;
      const inSelection = r >= r1 && r <= r2 && c >= c1 && c <= c2;
      if (r === r1 && c === c1) newRow.push(mergedAnchor);
      else if (!inSelection) newRow.push(cell);
      c += span;
    }
    return newRow;
  });

  return { ...table, rows: newRows };
}

export function splitCell(
  table: TableBlock,
  r: number,
  c: number,
): TableBlock {
  const colCount = gridColumnCount(table);
  const grid = expandTableGrid(table.rows, colCount);
  const gc = grid[r]?.[c];
  if (!gc || !gc.isAnchor) return table;
  const rowSpan = gc.cell.rowSpan ?? 1;
  const colSpan = gc.cell.colSpan ?? 1;
  if (rowSpan === 1 && colSpan === 1) return table;

  const newAnchor: TableCell = { text: gc.cell.text };

  const newRows: TableCell[][] = table.rows.map((row, rr) => {
    if (rr < r || rr >= r + rowSpan) return row;
    if (rr === r) {
      const out: TableCell[] = [];
      let cc = 0;
      for (const cell of row) {
        while (cc < colCount && grid[rr]![cc]!.isAnchor === false) cc += 1;
        const span = cell.colSpan ?? 1;
        if (cc === c) {
          out.push(newAnchor);
          for (let i = 1; i < colSpan; i += 1) out.push({ text: '' });
        } else {
          out.push(cell);
        }
        cc += span;
      }
      return out;
    }
    const out: TableCell[] = [];
    let cc = 0;
    let inserted = false;
    for (const cell of row) {
      while (cc < colCount && grid[rr]![cc]!.isAnchor === false) cc += 1;
      if (cc >= c && !inserted) {
        for (let i = 0; i < colSpan; i += 1) out.push({ text: '' });
        inserted = true;
      }
      out.push(cell);
      cc += cell.colSpan ?? 1;
    }
    if (!inserted) {
      for (let i = 0; i < colSpan; i += 1) out.push({ text: '' });
    }
    return out;
  });

  return { ...table, rows: newRows };
}

export function setHeaderRowCount(
  table: TableBlock,
  n: number,
): TableBlock {
  if (n <= 0) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { headerRowCount: _omit, ...rest } = table;
    return rest;
  }
  return { ...table, headerRowCount: n };
}

export function setRowHeaderCol(table: TableBlock, v: boolean): TableBlock {
  if (!v) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { rowHeaderCol: _omit, ...rest } = table;
    return rest;
  }
  return { ...table, rowHeaderCol: true };
}

export function setColumnAlign(
  table: TableBlock,
  c: number,
  align: 'left' | 'center' | 'right',
): TableBlock {
  const colCount = gridColumnCount(table);
  const columns = ensureColumnsArray(table, colCount);
  columns[c] = { ...columns[c], align };
  return { ...table, columns };
}

export function setColumnWidth(
  table: TableBlock,
  c: number,
  width: string,
): TableBlock {
  const colCount = gridColumnCount(table);
  const columns = ensureColumnsArray(table, colCount);
  const normalizedWidth = normalizeColumnWidthInput(width);
  const current: TableColumn = { ...(columns[c] ?? {}) };
  if (normalizedWidth === '') {
    delete current.width;
    columns[c] = current;
  } else {
    columns[c] = { ...current, width: normalizedWidth };
  }
  return { ...table, columns };
}

export function setCellText(
  table: TableBlock,
  r: number,
  c: number,
  text: string,
): TableBlock {
  const row = table.rows[r];
  if (!row) return table;
  const cell = row[c];
  if (!cell) return table;
  if (cell.text === text) return table;

  // Clone only the rows array, the touched row, and the touched cell —
  // leave every other row/cell identity intact so React.memo can skip them.
  const nextRow = row.slice();
  nextRow[c] = { ...cell, text };
  const nextRows = table.rows.slice();
  nextRows[r] = nextRow;
  return { ...table, rows: nextRows };
}

export function setCaption(table: TableBlock, caption: string): TableBlock {
  return { ...table, caption };
}
